import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useMayOpen } from '@/features/auth/access'
import { DataTable, type Column } from '@/components/DataTable'
import { ExportButton } from '@/components/ExportButton'
import { QueryState } from '@/components/QueryState'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import {
  exportBalances,
  formatCost,
  formatMoney,
  getValuation,
  listAllProducts,
  listLocations,
  listLowStock,
  type Balance,
  type ValuationLine,
} from '@/features/inventory/api'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

function Total({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
        {value}
      </p>
      {hint && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
    </div>
  )
}

/**
 * What the stock on hand is worth, at weighted average cost.
 *
 * The figure to read against the Inventory account on the trial balance: both sides are
 * built from the same movements, so a difference means a posting that did not happen or
 * one that happened twice. The screen says so rather than leaving the two numbers on
 * different pages for somebody to notice.
 *
 * Low stock sits underneath because it is the same table asked a different question, and
 * whoever is checking the value of the shelf is the person who wants to know what is about
 * to run out of it.
 */
export function StockValuation() {
  const [locationId, setLocationId] = useState('')

  const locations = useQuery({
    queryKey: ['stock-locations'],
    queryFn: listLocations,
    retry: false,
    staleTime: 5 * 60_000,
  })
  const products = useQuery({
    queryKey: ['products', 'all'],
    queryFn: () => listAllProducts(),
    retry: false,
    staleTime: 5 * 60_000,
  })
  const valuation = useQuery({
    queryKey: ['valuation', locationId],
    queryFn: () => getValuation(locationId || undefined),
    retry: false,
  })
  const lowStock = useQuery({
    queryKey: ['low-stock', locationId],
    queryFn: () => listLowStock(locationId || undefined),
    retry: false,
  })

  const productOf = (id: string) => products.data?.find((product) => product.id === id)
  const nameOf = (id: string) => {
    const product = productOf(id)
    return product ? `${product.code} · ${product.name}` : id.slice(0, 8)
  }
  const locationOf = (id: string) =>
    locations.data?.data.find((location) => location.id === id)?.name ?? '—'

  // A cashier and a field rep can both read stock without holding the catalog, so the
  // product name is a link only for someone the product screen would actually let in.
  const mayOpenProduct = useMayOpen('/products/:productId')
  const productLink = (id: string) =>
    mayOpenProduct ? (
      <Link to={`/products/${id}`} className="hover:underline">
        {nameOf(id)}
      </Link>
    ) : (
      nameOf(id)
    )

  const valuationColumns = useMemo<Column<ValuationLine>[]>(
    () => [
      {
        key: 'product',
        header: 'Product',
        className: 'font-medium',
        sortValue: (line) => nameOf(line.product_id),
        cell: (line) => productLink(line.product_id),
      },
      {
        key: 'location',
        header: 'Location',
        className: 'text-slate-500 dark:text-slate-400',
        sortValue: (line) => locationOf(line.location_id),
        cell: (line) => locationOf(line.location_id),
      },
      {
        key: 'quantity',
        header: 'On hand',
        className: 'text-right tabular-nums',
        headerClassName: 'text-right',
        sortValue: (line) => line.quantity,
        cell: (line) => line.quantity,
      },
      {
        key: 'avg_cost',
        header: 'Avg cost',
        className: 'text-right tabular-nums text-slate-500 dark:text-slate-400',
        headerClassName: 'text-right',
        sortValue: (line) => Number(line.avg_cost),
        cell: (line) => formatCost(line.avg_cost),
      },
      {
        key: 'value',
        header: 'Value',
        className: 'text-right font-medium tabular-nums',
        headerClassName: 'text-right',
        sortValue: (line) => Number(line.value),
        cell: (line) => formatMoney(line.value),
      },
    ],
    // The lookups close over two queries; rebuilding the columns when either lands is what
    // turns the ids into names on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products.data, locations.data],
  )

  const lowStockColumns = useMemo<Column<Balance>[]>(
    () => [
      {
        key: 'product',
        header: 'Product',
        className: 'font-medium',
        sortValue: (balance) => nameOf(balance.product_id),
        cell: (balance) => productLink(balance.product_id),
      },
      {
        key: 'location',
        header: 'Location',
        className: 'text-slate-500 dark:text-slate-400',
        cell: (balance) => locationOf(balance.location_id),
      },
      {
        key: 'quantity',
        header: 'On hand',
        className: 'text-right font-medium tabular-nums text-danger',
        headerClassName: 'text-right',
        sortValue: (balance) => balance.quantity,
        cell: (balance) => balance.quantity,
      },
      {
        key: 'reserved',
        header: 'Reserved',
        className: 'text-right tabular-nums text-slate-500 dark:text-slate-400',
        headerClassName: 'text-right',
        cell: (balance) => balance.reserved_quantity,
      },
      {
        key: 'reorder_point',
        header: 'Reorder at',
        className: 'text-right tabular-nums',
        headerClassName: 'text-right',
        sortValue: (balance) => balance.reorder_point,
        cell: (balance) => balance.reorder_point,
      },
      {
        key: 'short',
        // How many to buy, not just that it is low. A reorder list that makes the reader
        // do the subtraction is a list that gets done wrong on a Friday afternoon.
        header: 'Short by',
        className: 'text-right tabular-nums',
        headerClassName: 'text-right',
        sortValue: (balance) => balance.reorder_point - balance.quantity,
        cell: (balance) => Math.max(balance.reorder_point - balance.quantity, 0),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products.data, locations.data],
  )

  // An inventory manager holds stock in full and the ledger not at all.
  const mayOpenTrialBalance = useMayOpen('/accounting/trial-balance')
  const lines = valuation.data?.lines ?? []
  const low = lowStock.data ?? []

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Stock valuation</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            At weighted average cost.
            {mayOpenTrialBalance && (
              <>
                {' '}
                Compare the total with the Inventory account on the{' '}
                <Link to="/accounting/trial-balance" className="underline">
                  trial balance
                </Link>
                .
              </>
            )}
          </p>
        </div>
        <div>
          <label className="sr-only" htmlFor="valuation-location">
            Location
          </label>
          <select
            id="valuation-location"
            className={SELECT_CLASS}
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
          >
            <option value="">Every location</option>
            {(locations.data?.data ?? []).map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>
        <ExportButton
          filename="stock_on_hand"
          selectClassName={SELECT_CLASS}
          onExport={(format) => exportBalances({ location_id: locationId || undefined }, format)}
          errorMessage="Could not export the stock position."
        />
      </header>

      <Card>
        <CardBody>
          <QueryState isLoading={valuation.isLoading} error={valuation.error}>
            <div className="grid grid-cols-1 gap-6 tablet:grid-cols-3">
              <Total
                label="Stock value"
                value={formatMoney(valuation.data?.total_value ?? '0')}
                hint={locationId ? locationOf(locationId) : 'All locations'}
              />
              <Total
                label="Units on hand"
                value={(valuation.data?.total_quantity ?? 0).toLocaleString()}
                hint={`${lines.length} product/location line${lines.length === 1 ? '' : 's'}`}
              />
              <Total
                label="Below reorder point"
                value={low.length.toLocaleString()}
                hint={low.length > 0 ? 'Needs buying' : 'Nothing to reorder'}
              />
            </div>
          </QueryState>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Valuation lines"
          description="Rounded per line and then added up, so the total matches what you get adding the column by hand."
        />
        <DataTable
          rows={lines}
          columns={valuationColumns}
          getRowId={(line) => `${line.product_id}:${line.location_id}`}
          caption="Stock valuation by product and location"
          isLoading={valuation.isLoading || products.isLoading}
          error={valuation.error}
          emptyMessage="Nothing on the shelf to value."
          search={{
            placeholder: 'Filter by product…',
            matches: (line, query) => nameOf(line.product_id).toLowerCase().includes(query),
          }}
          pageSize={25}
        />
      </Card>

      <Card>
        <CardHeader title="Low stock" description="At or below the reorder point, worst first." />
        <DataTable
          rows={low}
          columns={lowStockColumns}
          getRowId={(balance) => balance.id}
          caption="Products at or below their reorder point"
          isLoading={lowStock.isLoading || products.isLoading}
          error={lowStock.error}
          emptyMessage="Nothing is below its reorder point."
          pageSize={25}
        />
      </Card>
    </div>
  )
}
